import { ClientsideLogger } from '@utils'
import { logRefTag } from './url'

const logger = new ClientsideLogger('DVWebNode:detail')
const IFRAME_ID = 'pv-hidden-iframe'
const WINDOW_BLUR_TIMEOUT_MILLISECONDS = 1000

type BrowserWindow = Window & { chrome: any, opera: any }
declare let window: BrowserWindow

const DesktopWindowsVersionRegex = /\bWindows NT ([\d.]+)\b/
export const canDeeplinkToWinApp = (ua: string) => {
  const match = ua.match(DesktopWindowsVersionRegex)
  const version = match && match[1]
  return parseInt(version, 10) >= 10
}


type Deeplink = { uri: string, appInstallUri: string }
export const deeplink = ({ uri, appInstallUri }: Deeplink) => {

  if (!uri) {
    logger.error('uri param is required for deeplinking')
    return
  }
  
  const customURI = _parseCustomURI(uri)
  const refTag = `dv_deeplink_${customURI}`

  const failCallback = () => {
    if (appInstallUri) {
      logRefTag(`${refTag}_noapp`)
      logger.count(`DVWeb::Deeplink::${customURI}::NoApp`, 1, true)
      navigateTo(appInstallUri)
    } else {
      logger.error('appInstallUri param is required for deeplinking')
    }
  }

  const sucessCallback = () => {
    logRefTag(refTag)
    logger.count(`DVWeb::Deeplink::${customURI}`, 1, true)
  }

  if (navigator.msLaunchUri) {
    // for Edge in Win 10
    navigator.msLaunchUri(uri, sucessCallback, failCallback)
  } else {
    if (document.hasFocus()) {
      openUri(uri, sucessCallback, failCallback)
    } else {
      const focusHandler = registerEvent(window, "focus", () => {
        focusHandler()
        openUri(uri, sucessCallback, failCallback)
      })
    }
  }
}


// Helpers

type DeeplinkHandler = (
  uri: string,
  sucessCallback: () => void,
  failCallback: () => void
) => void
const openUri: DeeplinkHandler = (uri, sucessCallback, failCallback) => {
  const browser = checkBrowser()
  if (browser.isChrome) {
    openUriWithTimeout(uri, sucessCallback, failCallback)
  } else if (browser.isFirefox) {
    openUriWithHiddenIFrame(uri, sucessCallback, failCallback)
  } else if (browser.isLegacyFirefox) {
    openUriUsingFirefox(uri, sucessCallback, failCallback)
  } else {
    logger.warn('Unexpected browser used for deeplinking', navigator.userAgent)
    openUriWithTimeout(uri, sucessCallback, failCallback)
  }
}

const openUriWithTimeout: DeeplinkHandler = (uri, sucessCallback, failCallback) => {
  const timeout = setTimeout(() => {
    failCallback()
    unregister && unregister()
  }, WINDOW_BLUR_TIMEOUT_MILLISECONDS)

  // handle page running in an iframe 
  // (blur must be registered with the top level window)
  let target: Window = window
  while (target != target.parent) {
    target = target.parent
  }

  const onBlur = () => {
    clearTimeout(timeout)
    unregister && unregister()
    sucessCallback()
  }
  const unregister = registerEvent(target, 'blur', onBlur)

  navigateTo(uri)
}

const openUriUsingFirefox: DeeplinkHandler = (uri, sucessCallback, failCallback) => {
  const iframe = getHiddenIframe()

  try {
    iframe.contentWindow.location.href = uri
    sucessCallback()
  } catch (e) {
    if (e.name === 'NS_ERROR_UNKNOWN_PROTOCOL') {
      failCallback()
    } else {
      logger.error('Unexpected error with Firefox deeplinking', e)
    }
  }
}

const openUriWithHiddenIFrame: DeeplinkHandler = (uri, sucessCallback, failCallback) => {
  const timeout = setTimeout(() => {
    failCallback()
    unregister && unregister()
  }, WINDOW_BLUR_TIMEOUT_MILLISECONDS)

  const iframe = getHiddenIframe()

  const onBlur = () => {
    clearTimeout(timeout)
    unregister && unregister()
    sucessCallback()
  }
  const unregister = registerEvent(window, "blur", onBlur)

  iframe.contentWindow.location.href = uri
}

const registerEvent = (target: Window, eventType: string, callback: () => void) => {
  target.addEventListener(eventType, callback)
  return () => {
    target.removeEventListener(eventType, callback)
  }
}

const getHiddenIframe = (): HTMLIFrameElement => (
  document.querySelector("#" + IFRAME_ID) || createHiddenIframe(document.body, "about:blank")
)

const createHiddenIframe = (target: Element, uri: string) => {
  const iframe = document.createElement('iframe')
  iframe.src = uri
  iframe.id = IFRAME_ID
  iframe.style.display = 'none'
  target.appendChild(iframe)
  return iframe
}

const checkBrowser = () => {
  const ue = navigator.userAgent.toLowerCase() || ""
  const isOpera = !!window.opera || !!ue.match(/opera|opr\//)
  const firefoxVersionMatch = ue.match(/firefox\/(\d+)/)
  const firefoxVersionString = firefoxVersionMatch && firefoxVersionMatch[1]
  const firefoxVersion = firefoxVersionString && parseInt(firefoxVersionString, 10)


  // These browser checks are limited to the Download Deeplinking use case on Win10 devices
  return {
    isOpera,
    isLegacyFirefox: firefoxVersion && firefoxVersion < 64,
    isFirefox: firefoxVersion && firefoxVersion >= 64,
    isChrome: !!window.chrome && !isOpera
  }
}

export const _parseCustomURI = (uri: string) => {
  const matches = uri && uri.match(/^([^:]+)/)
  return matches && matches[1] || 'unknown'
}

const navigateTo = (uri: string) => (window.location.href = uri)
